<template>
   <PageHeader title="Overview" :sticky="true" />
   <div id="main" class="q-pa-md bg-white q-pt-lg">
      <div class="row q-col-gutter-md">
         <div class="col-12 col-lg-4">
            <ConversionLiftCard
               id="conversion-lift-card"
               :loading="overviewLoading"
               :data="conversionLiftCardData"
            />
         </div>
         <div class="col-12 col-md-6 col-lg-4">
            <AdditionalConversionsCard
               id="additional-conversions-card"
               :loading="overviewLoading"
               :data="additionalConversionsCardData"
            />
         </div>
         <div class="col-12 col-md-6 col-lg-4">
            <TopConverterServingRatesCard
               id="top-converter-card"
               :loading="overviewLoading"
               :data="
                  overview?.top_converter_serving_rates_card ?? {
                     segments: [],
                  }
               "
            />
         </div>
      </div>
      <OptimizedVsDefaultGraphCard
         id="optimized-vs-default-graph-card"
         :loading="overviewLoading"
         :data="overview?.opt_v_default_graph_card"
      />
   </div>
</template>
<script setup lang="ts">
import { ref, onMounted, watch, toRefs, nextTick, computed } from "vue"
import { useAnalyticsStore } from "@stores/analyticsStore"
import { useQuasar } from "quasar"
import PageHeader from "@/components/common/PageHeader.vue"
import TopConverterServingRatesCard from "@components/overview/TopConverterServingRatesCard.vue"
import ConversionLiftCard from "@components/overview/ConversionLiftCard.vue"
import AdditionalConversionsCard from "../components/overview/AdditionalConversionsCard.vue"
import OptimizedVsDefaultGraphCard from "../components/overview/OptimizedVsDefaultGraphCard.vue"
import { callWithLoading } from "@utils/callWithLoading"
import { useTour } from "@/composables/useTour"
import { overviewTour } from "@/tours/overviewTour"
import { useProjectStore } from "@stores/projectStore"

// Utilities
const $q = useQuasar()

const analyticsStore = useAnalyticsStore()
const projectStore = useProjectStore()
const overviewLoading = ref(false)

// Destructure the overview property from the store to make it accessible in the template
const { overview, utmSource } = toRefs(analyticsStore)

// Initialize the tour
const { autoStartTour } = useTour(overviewTour)

// Computed data for components
const conversionLiftCardData = computed(() => {
   if (!overview.value?.opt_v_default_card) {
      return {
         ezbot_overall_rate: 0,
         ezbot_lb: 0,
         ezbot_ub: 0,
         default_overall_rate: 0,
         default_lb: 0,
         default_ub: 0,
      }
   }

   return {
      ...overview.value.opt_v_default_card,
      default_overall_rate:
         overview.value.opt_v_default_card.default_overall_rate ?? 0,
   }
})

const additionalConversionsCardData = computed(() => {
   if (!overview.value?.addl_conversions_card) {
      return {
         addl_conversions: 0,
         ezbot_sessions: 0,
         ezbot_conversions: 0,
         default_cr: 0,
         ezbot_cr: 0,
      }
   }

   const { ezbot_sessions, ezbot_cr } = overview.value.addl_conversions_card
   const defaultOverallRate =
      overview.value.opt_v_default_card?.default_overall_rate ?? 0
   const ezbotConversions = Math.round(ezbot_sessions * ezbot_cr)
   const defaultConversions = Math.round(ezbot_sessions * defaultOverallRate)

   return {
      addl_conversions: ezbotConversions - defaultConversions,
      ezbot_sessions,
      ezbot_conversions: ezbotConversions,
      default_cr: defaultOverallRate,
      ezbot_cr,
   }
})

onMounted(async () => {
   overviewLoading.value = true
   await callWithLoading(analyticsStore.fetchOverview, $q)
   if (overview.value) {
      overviewLoading.value = false
      await nextTick()
      await autoStartTour()
   }
})

// Watch for UTM source changes
watch(utmSource, async () => {
   overviewLoading.value = true
   await callWithLoading(analyticsStore.fetchOverview, $q)
   if (overview.value) {
      overviewLoading.value = false
   }
})

// Watch for date range changes from analytics store
watch(
   [() => analyticsStore.start_date, () => analyticsStore.end_date],
   async () => {
      overviewLoading.value = true
      await callWithLoading(analyticsStore.fetchOverview, $q)
      if (overview.value) {
         overviewLoading.value = false
      }
   },
)
// Watch for projectId changes from the project picker
watch(
   () => projectStore.projectId,
   async () => {
      overviewLoading.value = true
      await callWithLoading(analyticsStore.fetchOverview, $q)
      if (overview.value) {
         overviewLoading.value = false
      }
   },
)
</script>
<style scoped lang="scss">
#main {
   min-height: 600px;
   display: flex;
   flex-direction: column;
   gap: 10px;
}
</style>
